/**
 * Hook to trigger sync when page regains focus
 * This ensures data is refreshed when user returns to the app
 */

import { useEffect, useRef } from 'react';

/**
 * Sync data when page becomes visible
 * @param {Function} onSync - Callback function to execute when page gains focus
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether the hook is enabled (default: true)
 * @param {number} options.debounce - Debounce time in ms (default: 1000)
 */
export function useSyncOnFocus(onSync, options = {}) {
  const {
    enabled = true,
    debounce = 1000
  } = options;

  const timeoutRef = useRef(null);
  const lastSyncRef = useRef(Date.now());

  useEffect(() => {
    if (!enabled || typeof onSync !== 'function') {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Check if enough time has passed since last sync
        const now = Date.now();
        const timeSinceLastSync = now - lastSyncRef.current;

        if (timeSinceLastSync < debounce) {
          // Debounce - wait before syncing
          timeoutRef.current = setTimeout(() => {
            console.log('[useSyncOnFocus] Syncing after focus...');
            onSync();
            lastSyncRef.current = Date.now();
          }, debounce - timeSinceLastSync);
        } else {
          // Enough time has passed, sync immediately
          console.log('[useSyncOnFocus] Syncing on focus...');
          onSync();
          lastSyncRef.current = now;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onSync, enabled, debounce]);
}
